/**
 * Shared building blocks for the admin detail panels (Wave-4 console):
 * KPI cards, the super-admin license editor, the device table and small
 * formatters. Used by both CustomerDetailPanel and LicenseDetailPanel so the
 * "see + control everything" surface stays consistent. React + zustand only.
 */
import { useEffect, useRef, useState } from "react";
import { useServices } from "../app/context.js";
import { ActionBar } from "../ui/ActionBar.js";
import { useSessionStore } from "../stores/session.js";
import { enumLabel } from "../format/labels.js";
import { CopyButton } from "../ui/CopyButton.js";
import { parseServerTs, serverTsDate } from "@genz/platform";
import { RowAction } from "../ui/RowAction.js";
import type {
  CreatedLicense,
  CustomerKpis,
  DeviceDetail,
  LicenseDetail,
  LicenseSummary,
} from "@genz/platform";

const TIERS = ["free", "pro", "enterprise"];

export function fmtDate(iso: string | null | undefined): string {
  const d = serverTsDate(iso);
  return d ? d.toLocaleDateString() : "—";
}

/** Compact human counts: 18234 → "18k", 6100 → "6.1k", 512 → "512". */
export function fmtNum(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

/** "online now" / "4h ago" / "2d ago" relative time from an ISO timestamp. */
export function fmtRelative(iso: string | null | undefined): string {
  const t = parseServerTs(iso);
  if (!t) return "—";
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 90) return "online now";
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** Uppercase section label (LICENSE / DEVICES …) above a detail block. */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="detail-section-label">{children}</p>;
}

/**
 * 2-col KPI cards: label · big value · optional sub-label.
 *
 * WS-Z5 (R9). `.stat-card` was an ALIAS of `.kpi-card` — two names, one shared
 * `.kpi-card, .stat-card` rule pair, two markups, rendered in the same admin drawer flow.
 * This was the alias's only call site, so it renders `.kpi-card` now and the alias has
 * nothing left rendering it. (Deleting the `.stat-card*` halves of the shared rules in
 * `styles/components.css` is Z0's — a page wave does not edit the shared sheet.)
 * `.stat-cards`, the two-column GRID, is not the alias and stays: `.kpi-grid` is a different
 * grid (auto-fit) that the other KPI surface wants.
 */
export type StatCard = { label: string; value: React.ReactNode; sub?: string; testid?: string };
export function StatCards({ cards }: { cards: StatCard[] }) {
  return (
    <div className="stat-cards" data-testid="stat-cards">
      {cards.map((c, i) => (
        <div className="kpi-card" key={c.testid ?? i} data-testid={c.testid}>
          <span className="kpi-card__label">{c.label}</span>
          <span className="kpi-card__value">{c.value}</span>
          {c.sub && <span className="kpi-card__sub">{c.sub}</span>}
        </div>
      ))}
    </div>
  );
}

/** Bordered label | value fact rows (right-aligned values), as in the detail drawers. */
export type FactRow = { label: string; value: React.ReactNode };
export function FactRows({ rows, testid }: { rows: FactRow[]; testid?: string }) {
  return (
    <div className="fact-rows" data-testid={testid ?? "fact-rows"}>
      {rows.map((r, i) => (
        <div className="fact-row" key={i}>
          <span className="fact-row__label">{r.label}</span>
          <span className="fact-row__value">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

export function fmtDateTime(iso: string | null | undefined): string {
  const d = serverTsDate(iso);
  return d ? d.toLocaleString() : "—";
}

/** YYYY-MM-DD for an <input type="date"> from an ISO timestamp. */
function toDateInput(iso: string | null | undefined): string {
  const d = serverTsDate(iso);
  return d ? d.toISOString().slice(0, 10) : "";
}

export function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "active"
      ? "badge--success"
      // `grace_lapsed` joins the warn tier beside `grace` (F-8). The cloud's rule is
      // `active = grace_until is None or grace_until >= now`, so `active` is true exactly
      // while the device is INSIDE its window — which makes `!active` the LAPSED state, not
      // the grace state. Both raw values are kept: `grace` is still what a customer record's
      // activation status carries, and `grace_lapsed` is what a device row means by `!active`.
      : status === "suspended" || status === "grace" || status === "grace_lapsed"
        ? "badge--warn"
        : status === "blocked" || status === "expired" || status === "revoked"
          ? "badge--danger"
          : "badge--neutral";
  // WS-B B-3: the LABEL is humanised; the class ladder above switches on the RAW value and
  // must keep doing so. AUD-6 IMP-4: this printed `active` beside a Templates page saying
  // `Active`, which reads as two states rather than one.
  return <span className={`badge ${cls}`} data-testid="detail-status">{enumLabel(status)}</span>;
}

export function KpiGrid({ kpis, devicesActive }: { kpis: CustomerKpis; devicesActive: number }) {
  const cards: { label: string; value: number | string; testid: string }[] = [
    { label: "Designs generated", value: kpis.designs_generated, testid: "kpi-designs" },
    { label: "Mock-ups made", value: kpis.views_rendered, testid: "kpi-views" },
    { label: "Exports", value: kpis.exports_total, testid: "kpi-exports" },
    { label: "Jobs (total)", value: kpis.jobs_total, testid: "kpi-jobs" },
    { label: "Live / running", value: kpis.jobs_running, testid: "kpi-running" },
    { label: "Active devices", value: devicesActive, testid: "kpi-devices" },
  ];
  return (
    <div className="kpi-grid" data-testid="kpi-grid">
      {cards.map((c) => (
        <div className="kpi-card" key={c.testid} data-testid={c.testid}>
          <span className="kpi-card__value">{c.value}</span>
          <span className="kpi-card__label">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Super-admin license editor — validity / seats / tier. Edits PATCH
 * `/api/admin/licenses/{id}`; tenant-admins see a read-only note instead (the
 * server enforces super_admin, the UI mirrors). `onSaved` returns the refreshed
 * detail so the parent can re-render.
 *
 * WS-Z5: `emphasis` exists because rule 7 counts primaries per VIEW and this form appears in
 * two. In the license drawer it IS the view's commit, so it is the primary there; inside the
 * customer detail it is one section of a page whose commit is "Save changes" in the bar, so
 * it is a secondary. The call site is where that is known, so the call site says it.
 */
export function LicenseEditForm({
  license,
  onSaved,
  emphasis = "secondary",
}: {
  license: LicenseSummary;
  onSaved: (d: LicenseDetail) => void;
  emphasis?: "primary" | "secondary";
}) {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [expires, setExpires] = useState(toDateInput(license.expires_at));
  const [seats, setSeats] = useState(String(license.seats));
  const [tier, setTier] = useState(license.tier);
  const [deviceCap, setDeviceCap] = useState(
    license.device_cap != null ? String(license.device_cap) : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * P0-2: the sibling `LicenseRenewControls` (and every other save on the panel)
   * REPLACES the `license` prop under this form, but the fields above are seeded once
   * at mount and there is no `key` to remount them. So "+1 year" → change Seats → Save
   * PATCHed the PRE-renewal `expires_at` back and silently undid the renewal.
   * Resync per field, and only when the SERVER value actually changed, so a genuinely
   * in-progress local edit (the seats the operator is mid-typing) is never clobbered.
   */
  const serverSeen = useRef({
    expires: toDateInput(license.expires_at),
    seats: String(license.seats),
    tier: license.tier,
    deviceCap: license.device_cap != null ? String(license.device_cap) : "",
  });
  useEffect(() => {
    const next = {
      expires: toDateInput(license.expires_at),
      seats: String(license.seats),
      tier: license.tier,
      deviceCap: license.device_cap != null ? String(license.device_cap) : "",
    };
    const prev = serverSeen.current;
    if (next.expires !== prev.expires) setExpires(next.expires);
    if (next.seats !== prev.seats) setSeats(next.seats);
    if (next.tier !== prev.tier) setTier(next.tier);
    if (next.deviceCap !== prev.deviceCap) setDeviceCap(next.deviceCap);
    serverSeen.current = next;
  }, [license.expires_at, license.seats, license.tier, license.device_cap]);

  if (!isSuper) {
    return (
      <p className="admin-tab__note" data-testid="license-edit-readonly">
        License validity, seats, tier and device limit are managed by a platform super-admin.
      </p>
    );
  }

  async function save(clearExpiry = false) {
    setBusy(true);
    setError(null);
    try {
      // Blank device-cap field → revert to the tier default; otherwise set the override.
      const capTrimmed = deviceCap.trim();
      const next = await client.updateLicense(license.id, {
        clear_expiry: clearExpiry,
        expires_at: clearExpiry ? null : expires ? `${expires}T23:59:59Z` : undefined,
        seats: Number(seats) || license.seats,
        tier,
        ...(capTrimmed
          ? { device_cap: Number(capTrimmed) }
          : { clear_device_cap: true }),
      });
      if (clearExpiry) setExpires("");
      onSaved(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="license-edit"
      data-testid="license-edit"
      onSubmit={(e) => {
        e.preventDefault();
        void save(false);
      }}
    >
      <div className="field">
        <label htmlFor={`lic-exp-${license.id}`}>Valid until</label>
        <input
          id={`lic-exp-${license.id}`}
          type="date"
          value={expires}
          onChange={(e) => setExpires(e.target.value)}
          data-testid="license-expires"
        />
      </div>
      <div className="field">
        <label htmlFor={`lic-seats-${license.id}`}>Seats</label>
        <input
          id={`lic-seats-${license.id}`}
          type="number"
          min={1}
          value={seats}
          onChange={(e) => setSeats(e.target.value)}
          data-testid="license-seats"
        />
      </div>
      <div className="field">
        <label htmlFor={`lic-devices-${license.id}`}>Devices (max)</label>
        <input
          id={`lic-devices-${license.id}`}
          type="number"
          min={1}
          value={deviceCap}
          onChange={(e) => setDeviceCap(e.target.value)}
          placeholder="tier default"
          data-testid="license-device-cap"
        />
        <span className="faint">Blank = use the tier's device limit.</span>
      </div>
      <div className="field">
        <label htmlFor={`lic-tier-${license.id}`}>Tier</label>
        <select
          id={`lic-tier-${license.id}`}
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          data-testid="license-tier"
        >
          {(TIERS.includes(tier) ? TIERS : [tier, ...TIERS]).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className="row">
        <button
          type="submit"
          className={`btn btn--sm${emphasis === "primary" ? " btn--primary" : ""}`}
          disabled={busy}
          data-testid="license-save"
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          disabled={busy}
          onClick={() => void save(true)}
          data-testid="license-perpetual"
        >
          Make perpetual
        </button>
      </div>
      {error && <p role="alert" data-testid="license-edit-error">{error}</p>}
    </form>
  );
}

/**
 * Assign a license to a customer who has NONE (super_admin). Mints a fresh license
 * (in the customer's tenant) with the chosen tier / seats / validity, then grants it
 * to the customer — so license settings are reachable straight from the customer
 * detail panel even before any license exists. `onAssigned` refreshes the parent.
 */
export function AssignLicenseInline({
  customerId,
  tenantId,
  onAssigned,
}: {
  customerId: string;
  tenantId: string | null;
  onAssigned: () => void;
}) {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [tier, setTier] = useState("pro");
  const [seats, setSeats] = useState("1");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isSuper) {
    return <p className="faint" data-testid="detail-no-license">No license assigned. A platform super-admin can grant one.</p>;
  }

  async function grant(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const lic = await client.createLicense({
        tier,
        seats: Number(seats) || 1,
        expires_at: expires ? `${expires}T23:59:59Z` : null,
        tenant_id: tenantId,
      });
      await client.assignLicense(lic.id, customerId);
      onAssigned();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="license-edit" onSubmit={grant} data-testid="assign-license-form">
      <p className="faint detail__full-row">No license assigned — grant one:</p>
      <div className="field">
        <label htmlFor={`al-tier-${customerId}`}>Tier</label>
        <select id={`al-tier-${customerId}`} value={tier} onChange={(e) => setTier(e.target.value)} data-testid="assign-tier">
          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`al-seats-${customerId}`}>Seats</label>
        <input id={`al-seats-${customerId}`} type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="assign-seats" />
      </div>
      <div className="field">
        <label htmlFor={`al-exp-${customerId}`}>Valid until</label>
        <input id={`al-exp-${customerId}`} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="assign-expires" />
      </div>
      {/* WS-Z5: a sub-form inside the customer detail, whose one primary is in its bar. */}
      <button type="submit" className="btn btn--sm" disabled={busy} data-testid="assign-submit">
        {busy ? "Granting…" : "Grant license"}
      </button>
      {error && <p role="alert" data-testid="assign-error">{error}</p>}
    </form>
  );
}

/**
 * Renew / extend a license's validity (super_admin) by a fixed window. Extends from
 * the later of {now, current expiry}, so renewing an already-expired license starts
 * the new window today and renewing a still-valid one stacks on top. Reuses
 * `updateLicense` (expires_at). Hidden for perpetual licenses (nothing to renew).
 */
export function LicenseRenewControls({
  license,
  onSaved,
}: {
  license: LicenseSummary;
  onSaved: (d: LicenseDetail) => void;
}) {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isSuper || !license.expires_at) return null; // perpetual → no renewal needed

  async function extend(days: number) {
    setBusy(days);
    setError(null);
    try {
      const now = Date.now();
      const current = parseServerTs(license.expires_at) || now;
      const base = Math.max(now, current);
      const next = new Date(base + days * 86_400_000);
      onSaved(await client.updateLicense(license.id, { expires_at: next.toISOString() }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const PRESETS: { label: string; days: number }[] = [
    { label: "+30 days", days: 30 },
    { label: "+90 days", days: 90 },
    { label: "+1 year", days: 365 },
  ];
  return (
    <div className="license-renew" data-testid="license-renew">
      <span className="faint">Renew / extend validity:</span>
      <div className="row">
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            className="btn btn--sm btn--ghost"
            disabled={busy !== null}
            onClick={() => void extend(p.days)}
            data-testid={`license-renew-${p.days}`}
          >
            {busy === p.days ? "…" : p.label}
          </button>
        ))}
      </div>
      {error && <p role="alert" data-testid="license-renew-error">{error}</p>}
    </div>
  );
}

/**
 * Generate a brand-new license (super_admin). Pick tier / seats / validity (and an
 * optional tenant); the server mints the key. The PLAINTEXT key is shown ONCE in a
 * copyable banner after creation — it's a bearer secret never re-revealed. `onCreated`
 * lets the parent refresh its license list.
 */
export function GenerateLicenseForm({ onCreated }: { onCreated?: () => void }) {
  const { client } = useServices();
  const [tier, setTier] = useState("pro");
  const [seats, setSeats] = useState("5");
  const [deviceCap, setDeviceCap] = useState("");
  const [expires, setExpires] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedLicense | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const cap = deviceCap.trim();
      const lic = await client.createLicense({
        tier,
        seats: Number(seats) || 1,
        expires_at: expires ? `${expires}T23:59:59Z` : null,
        tenant_id: tenantId.trim() || null,
        ...(cap ? { device_cap: Number(cap) } : {}),
      });
      setCreated(lic);
      onCreated?.();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="admin-tab__generate" data-testid="license-generate">
      {/* F-3: the SUMMARY is the control — the 32px floor sits on it, and it is what
          `design-gate.mjs`'s `07-admin-activation` route names in `requires`. The `<details>`
          around it survives an empty body, so naming the container would let the route keep
          measuring after the disclosure it exists for had gone. */}
      <summary data-testid="license-generate-summary"><strong>Generate a license</strong></summary>
      <form className="license-edit" onSubmit={submit} data-testid="license-generate-form">
        <div className="field">
          <label htmlFor="gen-tier">Tier</label>
          <select
            id="gen-tier"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            data-testid="license-generate-tier"
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="gen-seats">Seats</label>
          <input
            id="gen-seats"
            type="number"
            min={1}
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            data-testid="license-generate-seats"
          />
        </div>
        <div className="field">
          <label htmlFor="gen-devices">Devices (max)</label>
          <input
            id="gen-devices"
            type="number"
            min={1}
            value={deviceCap}
            onChange={(e) => setDeviceCap(e.target.value)}
            placeholder="tier default"
            data-testid="license-generate-device-cap"
          />
        </div>
        <div className="field">
          <label htmlFor="gen-exp">Valid until</label>
          <input
            id="gen-exp"
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            data-testid="license-generate-expires"
          />
          <span className="faint">Leave blank for a perpetual license.</span>
        </div>
        <div className="field">
          <label htmlFor="gen-tenant">Tenant id (optional)</label>
          <input
            id="gen-tenant"
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="platform license if blank"
            data-testid="license-generate-tenant"
          />
        </div>
        {/* Rule 7 — Activation's one commit, in the bar at the bottom of the form. */}
        <ActionBar
          label="License generation actions"
          primary={
            <button type="submit" className="btn btn--primary" disabled={busy} data-testid="license-generate-submit">
              {busy ? "Generating…" : "Generate license"}
            </button>
          }
        />
        {error && <p role="alert" data-testid="license-generate-error">{error}</p>}
      </form>

      {created && (
        <div className="admin-tab__note" data-testid="license-generate-result">
          <p>
            <strong>License created.</strong> Copy this key now — it won't be shown in full again:
          </p>
          <span className="id-inline">
            <code data-testid="license-generate-key">{created.license_key}</code>
            <CopyButton value={created.license_key} label="Copy license key">Copy</CopyButton>
          </span>
        </div>
      )}
    </details>
  );
}

/**
 * THE admin console's activated-devices list (WS-Z9 / coordinator's Rule-A consolidation).
 *
 * There were three of these. This table — exported, never mounted — and a hand-rolled
 * `<ul className="device-cards">` in `CustomerDetailPanel` and another in
 * `LicenseDetailPanel`, both carrying `data-testid="detail-devices"`: the same test hook on
 * two different shapes, which is how a testid stops meaning anything. The two card lists are
 * gone and both panels mount this.
 *
 * It carries the SUPERSET of what the three showed, so nothing was lost to the merge:
 *
 * | datum / control     | old table | customer cards | license cards | here |
 * |---------------------|-----------|----------------|---------------|------|
 * | label / device id   | ✓         | ✓              | ✓             | ✓    |
 * | activation state    | ✓         | ✓              | ✓             | ✓    |
 * | fingerprint + copy  | ✓         | ✓              | ✓             | ✓    |
 * | last seen           | absolute  | relative       | —             | both |
 * | grace until         | ✓         | —              | —             | ✓    |
 * | Manage → drawer     | —         | ✓              | ✓             | ✓    |
 * | Deactivate          | ✓         | —              | —             | ✓    |
 *
 * `last seen` is one datum in two renderings and keeps both: the relative form is what an
 * operator scans, the absolute is one hover away. The state badge reads **Active / Grace
 * lapsed** — the cards said "Grace", which names the window the device is INSIDE when it is
 * active, so it said the opposite of what it meant on the row it appeared on.
 *
 * Both actions are optional and each panel passes what it offers: the customer and license
 * drawers open a device drawer (`Manage`), the activation surfaces deactivate. A panel that
 * passes neither gets a read-only table with no action column at all.
 *
 * `manageTestId` is required BY THE TYPE whenever `onManage` is given (F-9), and it exists
 * because the two panels' Manage buttons had different testids before this merge
 * (`customer-device-manage`, `license-device-manage`). Rule A: no test hook disappears, so the
 * caller names its own — and cannot forget to.
 */
/** What every caller passes, whichever actions it offers. */
interface DeviceTableBase {
  devices: DeviceDetail[];
  /** Admin deactivation — should call `client.adminDeactivateDevice` then refresh the parent. */
  onDeactivate?: (deviceId: string) => void;
  busyId?: string | null;
  emptyText?: string;
}

/**
 * MANAGE AND ITS TESTID ARE ONE DECISION, AND THE TYPE SAYS SO (F-9).
 *
 * `manageTestId` was documented as required and typed `manageTestId?: string`, so deleting a
 * caller's pass-through compiled and silently renamed that panel's Manage button to a default
 * nobody was querying — the exact class of break Rule A exists to prevent, shipped past
 * `tsc`. A prop whose doc comment and whose type disagree is enforced by neither.
 *
 * A union instead: offer `onManage` and you MUST name the testid it had before this table
 * merged the two panels' lists; offer neither and you may pass neither. There is no default,
 * because a default is what let the name go missing without anyone being asked.
 */
type DeviceTableProps = DeviceTableBase &
  (
    | { onManage: (device: DeviceDetail) => void; manageTestId: string }
    | { onManage?: never; manageTestId?: never }
  );

export function DeviceTable(props: DeviceTableProps) {
  const {
    devices,
    onDeactivate,
    onManage,
    manageTestId,
    busyId = null,
    emptyText = "No activated devices.",
  } = props;
  if (devices.length === 0) {
    return <p className="faint" data-testid="detail-no-devices">{emptyText}</p>;
  }
  const hasActions = !!onManage || !!onDeactivate;
  return (
    <div className="table-scroll">
    <table className="table" data-testid="detail-devices">
      <thead>
        <tr>
          <th>Device</th><th>Fingerprint</th><th>Last seen</th><th>Grace</th>
          {hasActions && <th />}
        </tr>
      </thead>
      <tbody>
        {devices.map((d) => (
          <tr key={d.device_id} data-testid="detail-device-row">
            <td>
              {d.label ?? d.device_id}{" "}
              <span className={`badge ${d.active ? "badge--success" : "badge--warn"}`}>
                {d.active ? "Active" : "Grace lapsed"}
              </span>
            </td>
            <td><span className="id-inline"><code>{d.fingerprint}</code><CopyButton value={d.fingerprint} label="Copy fingerprint" /></span></td>
            {/* One datum, two renderings: the relative form is what an operator scans down a
                column, and the exact timestamp the old table printed is one hover away. */}
            <td><span title={fmtDateTime(d.last_seen_at)}>{fmtRelative(d.last_seen_at)}</span></td>
            <td>{fmtDate(d.grace_until)}</td>
            {hasActions && (
              <td>
                {onManage && (
                  <RowAction
                    label="Manage"
                    hint="Manage this device"
                    icon="arrow-right"
                    tone="quiet"
                    onClick={() => onManage(d)}
                    data-testid={manageTestId}
                  />
                )}
                {onDeactivate && (
                  <RowAction
                    label="Deactivate"
                    icon="power"
                    tone="danger"
                    onClick={() => onDeactivate(d.device_id)}
                    disabled={busyId === d.device_id}
                    busy={busyId === d.device_id}
                    data-testid="detail-device-deactivate"
                  />
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

/** License summary facts row (key / tier / seats / devices / validity). */
export function LicenseFacts({ license }: { license: LicenseSummary }) {
  return (
    <dl className="detail-facts" data-testid="license-facts">
      <div><dt>Key</dt><dd><span className="id-inline"><code>{license.license_key_masked}</code><CopyButton value={license.license_key_masked} label="Copy license key" /></span></dd></div>
      <div><dt>Tier</dt><dd><span className="badge badge--info">{enumLabel(license.tier)}</span></dd></div>
      <div><dt>Seats used</dt><dd><span className="num-inline">{license.seats_used} / {license.seats}</span></dd></div>
      <div>
        <dt>Devices (max)</dt>
        <dd>{license.device_cap != null ? <span className="num-inline">{license.device_cap}</span> : "tier default"}</dd>
      </div>
      <div><dt>Active devices</dt><dd><span className="num-inline">{license.devices_active}</span></dd></div>
      <div>
        <dt>Validity</dt>
        <dd>
          {license.expires_at ? fmtDate(license.expires_at) : "Perpetual"}{" "}
          {license.expired && <span className="badge badge--danger">expired</span>}
        </dd>
      </div>
    </dl>
  );
}
